/**
 * Templates (brief §5.3): the removed Asian Trails modules come back as
 * templates, and a team can get a module of its own.
 *
 * Two kinds are installable from Admin ▸ Modules today:
 *
 *   code     The built-in code, switched on, named, placed and configured.
 *            Backed by fixed tables, so ONE instance per workspace. What it
 *            switches on (its module and pages) and its name are ordinary
 *            overrides of the module config; its `installed` entry holds
 *            where it lives (a parent module makes it a sub-module) and the
 *            settings the code reads.
 *   config   The "Blank team module": a Hot Plate board scoped to its id and
 *            My Notes, mounted at `/m/<id>` (the light-module pattern of
 *            brief §2.5; `hot_plate_tasks.mod` is free text, so no migration).
 *
 * Spec templates arrive with the module runtime (M3) and are listed as
 * "coming next"; integrations are set up where their provider is.
 *
 * Everything here is a pure function of a catalog and a config, so the page
 * can preview a change before it is saved, and the tests can pin it.
 */
import type { Catalog, CatalogModule, CatalogTemplate, ModuleTree, TreeModule, TreePage } from './catalog';
import {
  TEAM_TEMPLATE,
  installedEntries,
  teamModules,
  type InstalledEntry,
  type TenantModuleConfig,
  type TeamModuleEntry,
} from './config';

// ── What each code template is made of ──────────────────────────────────

/**
 * The pages each code template brings, first page = the one that says
 * whether it is in use. `templates.test.ts` holds every id to the catalog
 * (and `page`, when the catalog names one, to the first entry).
 *
 * Partner scorecards and the key partner report share the scorecard page
 * (the monthly report is a tab of it), so they come and go together.
 */
export const TEMPLATE_PAGES: Readonly<Record<string, readonly string[]>> = {
  'bug-tracker': ['lemax.issues', 'lemax.bug-report', 'lemax.hot-plate', 'lemax.my-notes'],
  'sop-library': ['lemax.sops'],
  'itinerary-checker': ['restools.itinerary-check'],
  'on-call-rota': ['coportal.hotline-schedule', 'coportal.hotline-schedule-edit'],
  'partner-scorecards': ['coportal.agent-scores', 'coportal.agent-scorecard'],
  'key-partner-report': ['coportal.agent-scorecard'],
};

/**
 * The settings the code reads today (the rest of a template's schema waits
 * for its rebuild as a spec, and the page says so). Each is read by the API:
 * the Bug tracker's notify address (LemaxIssuesController), the Itinerary
 * checker's rules and report format (ItineraryCheckAnalyzer), the rota's
 * report recipient (HotlineScheduleController).
 */
export const TEMPLATE_WIRED: Readonly<Record<string, readonly string[]>> = {
  'bug-tracker': ['notifyEmail'],
  'itinerary-checker': ['reportFormat', 'operatingRules'],
  'on-call-rota': ['reportEmail'],
};

/** Plain words for what a setting replaces, shown under its field. */
export const SETTING_HINT: Readonly<Record<string, string>> = {
  'bug-tracker.notifyEmail': 'Every new bug and update is copied here. Empty: the workspace mailbox in Settings.',
  'itinerary-checker.reportFormat': 'The operations report people upload, by name.',
  'itinerary-checker.operatingRules':
    'Where you operate and what you never sell. The checker reads these instead of the built-in rules.',
  'on-call-rota.reportEmail': 'The monthly allowance report goes here. Empty: the HR address in Settings.',
};

export function isInstallable(t: CatalogTemplate): boolean {
  return t.kind === 'code' || t.kind === 'config';
}

function pageIdsOf(t: CatalogTemplate): readonly string[] {
  return TEMPLATE_PAGES[t.id] ?? (t.page ? [t.page] : []);
}

function pageOf(m: CatalogModule, id: string) {
  for (const g of m.groups) for (const p of g.pages) if (`${m.id}.${p.slug}` === id) return p;
  return undefined;
}

/** A module-backed template names its module; a page-backed one names a page. */
function isModuleBacked(t: CatalogTemplate): boolean {
  return !t.page;
}

// ── State ───────────────────────────────────────────────────────────────

export interface TemplateState {
  template: CatalogTemplate;
  /** Its code is switched on in this workspace. */
  active: boolean;
  /** What people read: its instance name, else its module's or page's. */
  name: string;
  /** The module it lives in (its own when not placed elsewhere). */
  place: string | null;
  entry?: InstalledEntry;
  /** The other code templates it shares a page with (the scorecard). */
  sharesWith: string[];
}

export function entryFor(cfg: TenantModuleConfig, templateId: string): InstalledEntry | undefined {
  return installedEntries(cfg).find((e) => e.template === templateId);
}

function treePage(tree: ModuleTree, id: string): TreePage | undefined {
  for (const m of tree.modules) for (const g of m.groups) for (const p of g.pages) if (p.id === id) return p;
  return undefined;
}

/** Is this code template in use (its first page on)? */
export function isTemplateActive(tree: ModuleTree, t: CatalogTemplate): boolean {
  const first = pageIdsOf(t)[0];
  if (!first) return false;
  return treePage(tree, first)?.enabled ?? false;
}

export function templateState(
  catalog: Catalog,
  tree: ModuleTree,
  cfg: TenantModuleConfig,
  t: CatalogTemplate,
): TemplateState {
  const entry = entryFor(cfg, t.id);
  const active = isTemplateActive(tree, t);
  const own = tree.modules.find((m) => m.id === t.module);
  const first = pageIdsOf(t)[0];
  const page = first ? treePage(tree, first) : undefined;
  const name =
    entry?.label ?? (isModuleBacked(t) ? (own?.label ?? t.title) : (page?.label ?? t.title));
  const mine = new Set(pageIdsOf(t));
  const sharesWith = catalog.templates
    .filter((o) => o.id !== t.id && o.kind === 'code' && pageIdsOf(o).some((p) => mine.has(p)))
    .map((o) => o.id);
  return {
    template: t,
    active,
    name,
    place: entry?.parent ?? t.module ?? null,
    ...(entry ? { entry } : {}),
    sharesWith,
  };
}

// ── Edits (pure: config in, config out) ────────────────────────────────

function clone(cfg: TenantModuleConfig): TenantModuleConfig {
  return JSON.parse(JSON.stringify(cfg)) as TenantModuleConfig;
}

function moduleOn(catalog: Catalog, cfg: TenantModuleConfig, id: string): boolean {
  const m = catalog.modules.find((x) => x.id === id);
  return cfg.modules[id]?.enabled ?? m?.default === 'on';
}

function setPageEnabled(cfg: TenantModuleConfig, id: string, on: boolean) {
  const po = { ...(cfg.pages[id] ?? {}) };
  if (on) delete po.enabled;
  else po.enabled = false;
  if (Object.keys(po).length) cfg.pages[id] = po;
  else delete cfg.pages[id];
}

/** Set (or, when it equals the catalog's, clear) one field of a module override. */
function setModuleField<K extends 'label' | 'icon' | 'ink'>(
  cfg: TenantModuleConfig,
  m: CatalogModule,
  key: K,
  value: string | undefined,
) {
  const mo = { ...(cfg.modules[m.id] ?? {}) };
  const base = key === 'label' ? m.label : key === 'icon' ? m.icon : m.ink;
  if (!value || value === base) delete mo[key];
  else mo[key] = value;
  if (Object.keys(mo).length) cfg.modules[m.id] = mo;
  else delete cfg.modules[m.id];
}

function setPageLabel(cfg: TenantModuleConfig, m: CatalogModule, id: string, value: string | undefined) {
  const base = pageOf(m, id)?.label;
  const po = { ...(cfg.pages[id] ?? {}) };
  if (!value || value === base) delete po.label;
  else po.label = value;
  if (Object.keys(po).length) cfg.pages[id] = po;
  else delete cfg.pages[id];
}

export interface TemplateInstall {
  /** The name people read (1-40). */
  label: string;
  /** Module-backed, in its own place: the module's icon and ink. */
  icon?: string;
  ink?: string;
  /** The module it lives in; null / its own module = its own place. */
  parent?: string | null;
  group?: string;
  /** Only the settings the code reads (`TEMPLATE_WIRED`), empty ones dropped. */
  config?: Record<string, unknown>;
}

/**
 * Use a code template: switch its pages on (and its module, when that was
 * off: then nothing else of the module comes with it), give it its name,
 * place and settings. Using it again edits the same instance (one per
 * workspace).
 */
export function installTemplate(
  catalog: Catalog,
  before: TenantModuleConfig,
  t: CatalogTemplate,
  opts: TemplateInstall,
  now: string = new Date().toISOString(),
): TenantModuleConfig {
  if (t.kind !== 'code' || !t.module) throw new Error(`${t.id} is not a code template`);
  const cfg = clone(before);
  const m = catalog.modules.find((x) => x.id === t.module);
  if (!m) throw new Error(`unknown module ${t.module}`);
  const mine = pageIdsOf(t);
  const wasOn = moduleOn(catalog, before, m.id);

  if (!wasOn) {
    cfg.modules[m.id] = { ...(cfg.modules[m.id] ?? {}), enabled: true };
    // The module comes back with this template only: every other top-level
    // page of it stays off (rights children follow their parent).
    const slugs = new Set(m.groups.flatMap((g) => g.pages.map((p) => p.slug)));
    for (const g of m.groups) {
      for (const p of g.pages) {
        const id = `${m.id}.${p.slug}`;
        if (mine.includes(id) || (p.rights?.parent && slugs.has(p.rights.parent))) continue;
        setPageEnabled(cfg, id, false);
      }
    }
  }
  for (const id of mine) setPageEnabled(cfg, id, true);
  if (cfg.modules[m.id]?.enabled === true && m.default === 'on') {
    const mo = { ...cfg.modules[m.id] };
    delete mo.enabled;
    if (Object.keys(mo).length) cfg.modules[m.id] = mo;
    else delete cfg.modules[m.id];
  }

  const parent = opts.parent && opts.parent !== m.id ? opts.parent : null;
  const label = opts.label.trim();
  if (!parent) {
    if (isModuleBacked(t)) {
      setModuleField(cfg, m, 'label', label);
      setModuleField(cfg, m, 'icon', opts.icon);
      setModuleField(cfg, m, 'ink', opts.ink);
    } else {
      setPageLabel(cfg, m, mine[0]!, label);
    }
  } else if (!isModuleBacked(t)) {
    setPageLabel(cfg, m, mine[0]!, label);
  }

  const entry: InstalledEntry = { template: t.id };
  if (parent) entry.parent = parent;
  if (parent && opts.group) entry.group = opts.group;
  entry.label = label;
  const wired = TEMPLATE_WIRED[t.id] ?? [];
  const settings: Record<string, unknown> = {};
  for (const k of wired) {
    const v = opts.config?.[k];
    if (typeof v === 'string' && v.trim()) settings[k] = v.trim();
  }
  if (Object.keys(settings).length) entry.config = settings;
  entry.at = entryFor(before, t.id)?.at ?? now;
  cfg.installed = [...cfg.installed.filter((e) => (e as InstalledEntry)?.template !== t.id), entry];
  return cfg;
}

/**
 * Stop using a code template: its pages go off (never its data). When it
 * was the last thing its module showed and the module exists only to carry
 * templates (Lemax), the module goes off too.
 */
export function removeTemplate(
  catalog: Catalog,
  tree: ModuleTree,
  before: TenantModuleConfig,
  t: CatalogTemplate,
): TenantModuleConfig {
  const cfg = clone(before);
  cfg.installed = cfg.installed.filter((e) => (e as InstalledEntry)?.template !== t.id);
  const m = catalog.modules.find((x) => x.id === t.module);
  if (!m) return cfg;
  const mine = new Set(pageIdsOf(t));
  const othersActive = catalog.templates.some(
    (o) => o.id !== t.id && o.kind === 'code' && o.module === m.id && isTemplateActive(tree, o) && !pageIdsOf(o).every((p) => mine.has(p)),
  );
  const carrier = (m.tags ?? []).includes('template-source');
  if (carrier && !othersActive) {
    cfg.modules[m.id] = { ...(cfg.modules[m.id] ?? {}), enabled: false };
    return cfg;
  }
  for (const id of mine) {
    const p = pageOf(m, id);
    if (p?.rights?.parent && [...mine].some((x) => x === `${m.id}.${p.rights!.parent}`)) continue;
    setPageEnabled(cfg, id, false);
  }
  return cfg;
}

// ── Team modules ────────────────────────────────────────────────────────

/** A team module id from a name: "Quality & Audit" → "quality-audit". */
export function slugFromName(name: string): string {
  const s = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 31)
    .replace(/-+$/g, '');
  return /^[a-z]/.test(s) ? s : s ? `team-${s}`.slice(0, 31) : '';
}

export function addTeamModule(
  before: TenantModuleConfig,
  team: Omit<TeamModuleEntry, 'at'>,
  now: string = new Date().toISOString(),
): TenantModuleConfig {
  const cfg = clone(before);
  const entry: InstalledEntry = { template: TEAM_TEMPLATE, id: team.id, label: team.label.trim(), icon: team.icon, ink: team.ink, at: now };
  cfg.installed = [...cfg.installed, entry];
  if (cfg.order.length && !cfg.order.includes(team.id)) cfg.order = [...cfg.order, team.id];
  return cfg;
}

export function updateTeamModule(
  before: TenantModuleConfig,
  id: string,
  patch: Partial<Pick<TeamModuleEntry, 'label' | 'icon' | 'ink'>>,
): TenantModuleConfig {
  const cfg = clone(before);
  cfg.installed = cfg.installed.map((e) => {
    const x = e as InstalledEntry;
    if (x?.template !== TEAM_TEMPLATE || x.id !== id) return e;
    return { ...x, ...patch, ...(patch.label ? { label: patch.label.trim() } : {}) };
  });
  return cfg;
}

export function removeTeamModule(before: TenantModuleConfig, id: string): TenantModuleConfig {
  const cfg = clone(before);
  cfg.installed = cfg.installed.filter((e) => !((e as InstalledEntry)?.template === TEAM_TEMPLATE && (e as InstalledEntry).id === id));
  cfg.order = cfg.order.filter((x) => x !== id);
  return cfg;
}

export { teamModules };

// ── Placing a template inside another module (resolution) ──────────────

/**
 * Move the rail rows of every code template placed inside another module
 * (brief §5.3: a parent makes it a SUB-MODULE). The routes and rights stay
 * where they are (§5.9); only the rail changes: the rows become a section of
 * the host (headed with the instance name) or join one of its sections, and
 * the route answers to the host's rail. A module left with no row of its own
 * is `hostedIn` its host: no Hub tile, no switcher entry.
 *
 * Called by `resolveModuleTree`, after every switch is applied.
 */
export function placeTemplates(catalog: Catalog, cfg: TenantModuleConfig, modules: TreeModule[]): void {
  const byId = new Map(modules.map((m) => [m.id, m]));
  for (const e of installedEntries(cfg)) {
    const t = catalog.templates.find((x) => x.id === e.template);
    if (!t || t.kind !== 'code' || !t.module || !e.parent || e.parent === t.module) continue;
    const own = byId.get(t.module);
    const host = byId.get(e.parent);
    if (!own || !host || !own.enabled || !host.enabled) continue;
    const mine = new Set(pageIdsOf(t));
    const moved: TreePage[] = [];
    own.groups = own.groups.map((g) => ({
      ...g,
      pages: g.pages.filter((p) => {
        if (!mine.has(p.id) || p.navHidden || !p.enabled) return true;
        moved.push({ ...p, requires: { ...(p.requires ?? {}), tile: own.tile } });
        return false;
      }),
    }));
    if (!moved.length) continue;
    const join = e.group ? host.groups.find((g) => g.id === e.group) : undefined;
    if (join) {
      host.groups = host.groups.map((g) => (g === join ? { ...g, pages: [...g.pages, ...moved] } : g));
    } else {
      host.groups = [
        ...host.groups,
        { id: `tpl-${t.id}`, heading: e.label ?? t.title, ink: own.ink, pages: moved },
      ];
    }
    const ownRows = own.groups.some((g) => g.pages.some((p) => p.enabled && !p.navHidden));
    if (!ownRows) own.hostedIn = host.id;
  }
}
